import { forwardRef } from "react";

export interface ToggleProps {
  /** Whether the toggle is on */
  checked: boolean;
  /** Callback when the toggle state changes */
  onChange: (checked: boolean) => void;
  /** Label text for the toggle */
  label: string;
  /** Optional description text */
  description?: string;
  /** Size variant */
  toggleSize?: "md" | "lg";
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Optional id for the input */
  id?: string;
  /** Optional className */
  className?: string;
}

/**
 * Toggle - An accessible, playful toggle switch for settings.
 *
 * Features a whimsical design with smooth animations and sparkle effects.
 * Meets WCAG accessibility standards with proper ARIA attributes.
 *
 * @example
 * <Toggle
 *   checked={musicEnabled}
 *   onChange={setMusicEnabled}
 *   label="Background Music"
 *   description="Play gentle melodies during gameplay"
 * />
 */
export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      checked,
      onChange,
      label,
      description,
      toggleSize = "lg",
      disabled = false,
      id,
      className = "",
    },
    ref
  ) => {
    const size = toggleSize;
    const toggleId = id || `toggle-${label.toLowerCase().replace(/\s+/g, "-")}`;

    const handleChange = () => {
      if (!disabled) {
        onChange(!checked);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleChange();
      }
    };

    // Size styles
    const trackSizes = {
      md: "w-14 h-8",
      lg: "w-16 h-9",
    };

    const thumbSizes = {
      md: "w-6 h-6",
      lg: "w-7 h-7",
    };

    const thumbPosition = {
      md: checked ? "translate-x-6" : "translate-x-1",
      lg: checked ? "translate-x-7" : "translate-x-1",
    };

    return (
      <div className={`flex items-start justify-between gap-4 py-1 ${className}`}>
        {/* Label and description */}
        <div className="flex-1">
          <label
            htmlFor={toggleId}
            className={`
              block font-body font-semibold text-body-lg text-neutral-800
              ${disabled ? "opacity-50" : "cursor-pointer"}
            `}
          >
            {label}
          </label>
          {description && (
            <p className="font-body text-body-md text-neutral-500 mt-0.5">
              {description}
            </p>
          )}
        </div>

        {/* Toggle switch */}
        <button
          ref={ref}
          type="button"
          role="switch"
          id={toggleId}
          aria-checked={checked}
          aria-label={label}
          disabled={disabled}
          onClick={handleChange}
          onKeyDown={handleKeyDown}
          className={`
            relative flex items-center flex-shrink-0
            ${trackSizes[size]}
            rounded-full
            transition-all duration-300 ease-out
            focus:outline-none focus-visible:ring-4 focus-visible:ring-magic-300 focus-visible:ring-offset-2
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            ${
              checked
                ? "bg-gradient-to-r from-meadow-400 via-meadow-500 to-meadow-400 shadow-[0_2px_8px_rgba(107,203,119,0.4),inset_0_1px_0_rgba(255,255,255,0.3)]"
                : "bg-neutral-200 shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
            }
          `}
        >
          {/* Track glow effect when checked */}
          {checked && !disabled && (
            <span
              className="absolute inset-0 rounded-full animate-pulse opacity-30"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(107,203,119,0.6) 0%, transparent 70%)",
              }}
            />
          )}

          {/* Thumb */}
          <span
            className={`
              absolute
              ${thumbSizes[size]}
              ${thumbPosition[size]}
              rounded-full
              bg-white
              shadow-[0_2px_6px_rgba(0,0,0,0.2),0_1px_2px_rgba(0,0,0,0.1)]
              transition-all duration-300
              ${checked ? "scale-105" : "scale-100"}
            `}
            style={{
              transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          >
            {/* Inner decoration */}
            <span
              className={`
                absolute inset-1 rounded-full
                transition-colors duration-300
                ${checked ? "bg-meadow-100" : "bg-neutral-100"}
              `}
            />

            {/* Sparkle on checked state */}
            {checked && !disabled && (
              <span className="absolute -top-1 -right-1 w-3 h-3 text-sunshine-400 animate-sparkle">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
                </svg>
              </span>
            )}
          </span>
        </button>
      </div>
    );
  }
);

Toggle.displayName = "Toggle";

export default Toggle;
